/**
 * Admin API Service
 *
 * Provides methods for admin operations on users and groups.
 * All operations require admin role and are proxied through the FastAPI backend.
 */

import { apiService } from "./api";
import {
  KeycloakGroupRepresentation,
  KeycloakUserRepresentation,
  GroupLayersResponse,
} from "../global/types/user";

// ============================================================================
// Types
// ============================================================================

interface GroupInfo {
  id: string;
  name: string;
  path?: string;
}

interface UserWithGroups extends KeycloakUserRepresentation {
  groups: GroupInfo[];
  roles: string[];
}

interface UsersListResponse {
  users: UserWithGroups[];
}

interface GroupsListResponse {
  groups: GroupWithLayers[];
}

interface GroupWithLayers {
  id: string;
  name: string;
  path?: string;
  allowedLayers: string[];
  attributes?: Record<string, string[]>;
}

interface ChangeUserGroupData {
  newGroupId: string | null;
  oldGroupId: string | null;
}

interface UserActionResponse {
  success: boolean;
  message: string;
}

interface GroupLayersUpdateResponse {
  id: string;
  name: string;
  allowedLayers: string[];
}

interface UpdateProfileData {
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface UpdateProfileResponse {
  success: boolean;
  message: string;
}

// ============================================================================
// Role Permission Types
// ============================================================================

export interface RolePermission {
  id: number;
  role_name: string;
  description?: string;
  allowed_layers: string[];
  allowed_features: string[];
}

export interface RolePermissionsListResponse {
  roles: RolePermission[];
}

export interface CreateRolePermissionRequest {
  role_name: string;
  description?: string;
  allowed_layers: string[];
  allowed_features: string[];
}

export interface UpdateRolePermissionRequest {
  description?: string;
  allowed_layers?: string[];
  allowed_features?: string[];
}

export interface UserPermissions {
  roles: string[];
  allowed_layers: string[];
  allowed_features: string[];
  has_wildcard: boolean;
  is_admin: boolean;
}

export interface AvailableFeaturesResponse {
  features: string[];
}

export interface ChangeUserRolesData {
  roles: string[];
}

// ============================================================================
// Audit Log Types
// ============================================================================

export type AuditAction = "CREATE" | "UPDATE" | "DELETE";

export interface AuditLogValues {
  description?: string;
  allowed_layers?: string[];
  allowed_features?: string[];
}

export interface AuditLogEntry {
  id: number;
  role_name: string;
  action: AuditAction;
  changed_by_keycloak_id: string;
  changed_by_email?: string;
  changed_at: string; // ISO 8601 datetime
  old_values?: AuditLogValues;
  new_values?: AuditLogValues;
}

export interface AuditLogResponse {
  total: number;
  entries: AuditLogEntry[];
}

// ============================================================================
// Error Types
// ============================================================================

export interface ValidationErrorDetail {
  type: string;
  loc: string[];
  msg: string;
  input: any;
}

export interface ValidationError {
  detail: ValidationErrorDetail[];
}

export interface BusinessError {
  detail: string;
}

export interface FieldError {
  field: string;
  message: string;
}

export interface ParsedError {
  fieldErrors?: FieldError[];
  general?: string;
}

// ============================================================================
// Constants
// ============================================================================

export const SYSTEM_ROLES = ["viewer", "specialist", "admin"] as const;
export type SystemRole = typeof SYSTEM_ROLES[number];

export const FEATURE_CATEGORIES = {
  viewer: ["view_stations", "view_statistics", "download_public_maps", "view_layers"],
  specialist: ["upload_shapefiles", "crop_layers", "reproject_layers", "export_data", "draw_features"],
  admin: ["manage_layer_catalog", "clear_cache", "manage_users", "manage_permissions"],
} as const;

export const FEATURE_LABELS: Record<string, string> = {
  // Viewer
  view_stations: "Visualizar estações",
  view_statistics: "Visualizar estatísticas",
  download_public_maps: "Download de mapas públicos",
  view_layers: "Visualizar camadas",
  // Specialist
  upload_shapefiles: "Upload de shapefiles",
  crop_layers: "Recortar camadas",
  reproject_layers: "Reprojetar camadas",
  export_data: "Exportar dados",
  draw_features: "Desenhar feições",
  // Admin
  manage_layer_catalog: "Gerenciar catálogo de camadas",
  clear_cache: "Limpar cache",
  manage_users: "Gerenciar usuários",
  manage_permissions: "Gerenciar permissões",
};

// ============================================================================
// Admin API Service
// ============================================================================

export const AdminApi = {
  // =========================================================================
  // Users
  // =========================================================================

  /**
   * Get all users with their group memberships
   */
  getUsers: async (): Promise<UserWithGroups[]> => {
    const response = await apiService.get<UsersListResponse>("/api/v1/admin/users");
    return response.data.users;
  },

  /**
   * Delete a user
   */
  deleteUser: async (userId: string): Promise<UserActionResponse> => {
    const response = await apiService.delete<UserActionResponse>(`/api/v1/admin/users/${userId}`);
    return response.data;
  },

  /**
   * Change a user's group membership
   */
  changeUserGroup: async (userId: string, data: ChangeUserGroupData): Promise<UserActionResponse> => {
    const response = await apiService.put<UserActionResponse>(
      `/api/v1/admin/users/${userId}/groups`,
      data
    );
    return response.data;
  },

  // =========================================================================
  // Groups
  // =========================================================================

  /**
   * Get all groups with their allowed layers
   */
  getGroups: async (): Promise<GroupWithLayers[]> => {
    const response = await apiService.get<GroupsListResponse>("/api/v1/admin/groups");
    return response.data.groups;
  },

  /**
   * Get a specific group's details
   */
  getGroup: async (groupId: string): Promise<GroupWithLayers> => {
    const response = await apiService.get<GroupWithLayers>(`/api/v1/admin/groups/${groupId}`);
    return response.data;
  },

  /**
   * Update allowed layers for a group
   */
  updateGroupLayers: async (groupId: string, allowedLayers: string[]): Promise<GroupLayersUpdateResponse> => {
    const response = await apiService.put<GroupLayersUpdateResponse>(
      `/api/v1/admin/groups/${groupId}/layers`,
      { allowedLayers }
    );
    return response.data;
  },

  /**
   * Change a user's roles
   */
  changeUserRoles: async (userId: string, roles: string[]): Promise<UserActionResponse> => {
    const response = await apiService.put<UserActionResponse>(
      `/api/v1/admin/users/${userId}/roles`,
      { roles }
    );
    return response.data;
  },

  // =========================================================================
  // Role Permissions
  // =========================================================================

  /**
   * Get all role permissions
   */
  getRolePermissions: async (): Promise<RolePermission[]> => {
    const response = await apiService.get<RolePermissionsListResponse>("/api/v1/permissions/roles");
    return response.data.roles;
  },

  /**
   * Get a specific role's permissions
   */
  getRolePermission: async (roleName: string): Promise<RolePermission> => {
    const response = await apiService.get<RolePermission>(
      `/api/v1/permissions/roles/${encodeURIComponent(roleName)}`
    );
    return response.data;
  },

  /**
   * Create a new role permission
   */
  createRolePermission: async (data: CreateRolePermissionRequest): Promise<RolePermission> => {
    const response = await apiService.post<RolePermission>("/api/v1/permissions/roles", data);
    return response.data;
  },

  /**
   * Update a role's permissions
   */
  updateRolePermission: async (roleName: string, data: UpdateRolePermissionRequest): Promise<RolePermission> => {
    const response = await apiService.put<RolePermission>(
      `/api/v1/permissions/roles/${encodeURIComponent(roleName)}`,
      data
    );
    return response.data;
  },

  /**
   * Delete a role permission (system roles cannot be deleted)
   */
  deleteRolePermission: async (roleName: string): Promise<UserActionResponse> => {
    const response = await apiService.delete<UserActionResponse>(
      `/api/v1/permissions/roles/${encodeURIComponent(roleName)}`
    );
    return response.data;
  },

  /**
   * Seed default role permissions
   */
  seedDefaultRoles: async (): Promise<RolePermission[]> => {
    const response = await apiService.post<RolePermissionsListResponse>("/api/v1/permissions/roles/seed");
    return response.data.roles;
  },

  /**
   * Get current user's effective permissions
   */
  getMyPermissions: async (): Promise<UserPermissions> => {
    const response = await apiService.get<UserPermissions>("/api/v1/permissions/me");
    return response.data;
  },

  /**
   * Get all available features that can be permission-controlled
   */
  getAvailableFeatures: async (): Promise<string[]> => {
    const response = await apiService.get<AvailableFeaturesResponse>("/api/v1/permissions/features");
    return response.data.features;
  },

  /**
   * Get audit history for a role
   */
  getRoleAuditHistory: async (
    roleName: string,
    limit = 50,
    offset = 0
  ): Promise<AuditLogResponse> => {
    const response = await apiService.get<AuditLogResponse>(
      `/api/v1/permissions/roles/${encodeURIComponent(roleName)}/audit`,
      { params: { limit, offset } }
    );
    return response.data;
  },

  // =========================================================================
  // User Self-Service
  // =========================================================================

  /**
   * Update current user's profile
   */
  updateUserProfile: async (updates: UpdateProfileData): Promise<UpdateProfileResponse> => {
    const response = await apiService.put<UpdateProfileResponse>(
      "/api/v1/auth/profile",
      updates
    );
    return response.data;
  },
};

// ============================================================================
// Legacy Compatibility Layer
// ============================================================================
// These functions maintain backward compatibility with the old keycloakAdmin.ts API
// They can be used as drop-in replacements in existing code

/**
 * Get all groups (legacy compatibility)
 * @deprecated Use AdminApi.getGroups() instead
 */
export const getGroups = async (_token: string): Promise<KeycloakGroupRepresentation[]> => {
  const groups = await AdminApi.getGroups();
  return groups.map(g => ({
    id: g.id,
    name: g.name,
    path: g.path,
    attributes: g.attributes || { allowed_layers: g.allowedLayers },
  }));
};

/**
 * Get a specific group by ID (legacy compatibility)
 * @deprecated Use AdminApi.getGroup() instead
 */
export const getGroupById = async (_token: string, groupId: string): Promise<KeycloakGroupRepresentation> => {
  const group = await AdminApi.getGroup(groupId);
  return {
    id: group.id,
    name: group.name,
    path: group.path,
    attributes: group.attributes || { allowed_layers: group.allowedLayers },
  };
};

/**
 * Update allowed layers for a group (legacy compatibility)
 * @deprecated Use AdminApi.updateGroupLayers() instead
 */
export const updateGroupLayers = async (
  _token: string,
  request: { groupId: string; allowedLayers: string[] }
): Promise<GroupLayersResponse> => {
  const result = await AdminApi.updateGroupLayers(request.groupId, request.allowedLayers);
  return {
    groupId: result.id,
    groupName: result.name,
    allowedLayers: result.allowedLayers,
  };
};

/**
 * Get all users (legacy compatibility)
 * @deprecated Use AdminApi.getUsers() instead
 */
export const getAllUsersDetailed = async (_token: string): Promise<KeycloakUserRepresentation[]> => {
  const users = await AdminApi.getUsers();
  return users.map(u => ({
    id: u.id,
    username: u.username,
    email: u.email,
    firstName: u.firstName,
    lastName: u.lastName,
    enabled: u.enabled,
    emailVerified: u.emailVerified,
    createdTimestamp: u.createdTimestamp,
  }));
};

/**
 * Get group members (legacy compatibility)
 * Note: This is now handled by the getUsers endpoint which includes group membership
 * @deprecated Use AdminApi.getUsers() instead
 */
export const getGroupMembers = async (_token: string, _groupId: string): Promise<KeycloakUserRepresentation[]> => {
  // The new API returns users with their groups, so we filter client-side
  const users = await AdminApi.getUsers();
  return users
    .filter(u => u.groups.some(g => g.id === _groupId))
    .map(u => ({
      id: u.id,
      username: u.username,
      email: u.email,
      firstName: u.firstName,
      lastName: u.lastName,
      enabled: u.enabled,
      emailVerified: u.emailVerified,
      createdTimestamp: u.createdTimestamp,
    }));
};

/**
 * Delete a user (legacy compatibility)
 * @deprecated Use AdminApi.deleteUser() instead
 */
export const deleteUser = async (_token: string, userId: string): Promise<void> => {
  await AdminApi.deleteUser(userId);
};

/**
 * Change user's group (legacy compatibility)
 * @deprecated Use AdminApi.changeUserGroup() instead
 */
export const changeUserGroup = async (
  _token: string,
  userId: string,
  newGroupId: string | null,
  oldGroupId: string | null
): Promise<void> => {
  await AdminApi.changeUserGroup(userId, { newGroupId, oldGroupId });
};

/**
 * Update user profile (legacy compatibility)
 * @deprecated Use AdminApi.updateUserProfile() instead
 */
export const updateUserProfile = async (
  _token: string,
  _userId: string,
  updates: { firstName?: string; lastName?: string; email?: string }
): Promise<void> => {
  await AdminApi.updateUserProfile(updates);
};

export default AdminApi;
